import CommentsBox from 'components/common/comments/CommentsBox';
import { Box } from '@mui/material';
import useUserData from 'hooks/useUserData';
import TrackerAPI from '@buildwise/libs/trackers/apis/TrackerAPI';
import { MentionType } from '@buildwise/pages/article/components/CommentsBox';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { LikedUserType } from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { getExactFileName } from 'utility/helpers';
import _ from 'lodash';
import { AxiosResponse } from 'axios';
import KnowledgeAPI from 'api/KnowledgeAPI';
import { CommentReplyType, CommentType, TaskType, useObjectCommentsQuery } from '../../../queries';

type TaskCommentsBoxProps = {
  comments: CommentType[];
  selectedTask: TaskType;
};

function TaskCommentsBox({ comments, selectedTask }: TaskCommentsBoxProps) {
  const { user, companyId } = useUserData();
  const { updateCommentsCache } = useObjectCommentsQuery({
    objectId: selectedTask.id,
    objectName: 'Task',
    objectType: 'Comment',
  });
  const handleAddComment = async (
    text: string,
    mentions: MentionType[],
    attachments: AttachmentType[],
  ) => {
    if (user) {
      const mentionPayload = mentions.map((m) => ({
        displayName: m.display,
        userName: m.id,
        pmuserId: m?.pmuserId,
        title: m.title,
        id: m.userId,
      }));

      const { data: commentResData } = await TrackerAPI.addCommentByObjectId({
        companyId,
        objectId: selectedTask.id,
        objectName: 'Task',
        comment: {
          commentAuthor: user.username,
          commentText: text,
          mentions: mentionPayload,
          objectType: 'Comment',
        },
      });

      if (attachments.length > 0 && commentResData?.comment?.id) {
        const formData = new FormData();
        attachments.forEach((attachment) => {
          formData.append('file', attachment.file);
          if (attachment.thumbnail) {
            formData.append('thumbnail', attachment.thumbnail);
          }
        });

        const { data: commentMediaResData } = await KnowledgeAPI.addCommentMedia(
          companyId,
          commentResData?.comment?.id,
          formData,
        );
        updateCommentsCache(commentMediaResData, 'add');
      } else {
        updateCommentsCache(commentResData?.comment, 'add');
      }
    }
  };

  const handleReplyComment = async (
    payload: any,
    replyAttachments: AttachmentType[],
    commentPath: string,
  ) => {
    const { data: replyCommentResData } = await KnowledgeAPI.replyComment({
      ...payload,
      objectId: selectedTask.id,
      objectName: 'Task',
      comment: {
        ...payload.comment,
        objectType: 'Comment',
      },
    });

    if (replyAttachments.length > 0 && replyCommentResData?.comment?.id) {
      const formData = new FormData();

      replyAttachments.forEach((attachment) => {
        formData.append('file', attachment.file);
        if (attachment.thumbnail) {
          formData.append('thumbnail', attachment.thumbnail);
        }
      });

      const { data: replyCommentMediaResData } = await KnowledgeAPI.addCommentMedia(
        companyId,
        replyCommentResData?.comment?.id,
        formData,
      );
      updateCommentsCache(replyCommentMediaResData, 'reply', commentPath);
    } else {
      updateCommentsCache(replyCommentResData?.comment, 'reply', commentPath);
    }
  };

  const handleUpdateComment = async (
    comment: CommentType | CommentReplyType,
    payload: any,
    newAttachments: AttachmentType[],
    updatedAttachments: AttachmentType[],
    removedAttachments: string[],
    editAttachments: AttachmentType[],
    commentPath: string,
  ) => {
    const updateCommentPromises: Promise<AxiosResponse<any, Error> | any>[] = [];

    // check if comment text can be updated even when it is empty with attachments
    const shouldUpdateCommentText = payload.comment.commentText
      ? payload.comment.commentText !== comment.commentText
      : comment.attachments?.length && comment.attachments?.length !== removedAttachments.length;
    if (shouldUpdateCommentText) {
      updateCommentPromises.push(
        KnowledgeAPI.updateComment({
          ...payload,
          objectId: selectedTask.id,
          objectName: 'Task',
          comment: { ...payload.comment, objectType: 'Comment' },
        }),
      );
    }

    if (newAttachments.length > 0) {
      const formData = new FormData();

      newAttachments.forEach((attachment) => {
        formData.append('file', attachment.file);
        if (attachment.thumbnail) {
          formData.append('thumbnail', attachment.thumbnail);
        }
      });

      updateCommentPromises.push(KnowledgeAPI.addCommentMedia(companyId, comment?.id, formData));
    }

    if (updatedAttachments.length > 0) {
      const formData = new FormData();

      updatedAttachments.forEach((attachment) => {
        formData.append('file', attachment.file);
        if (attachment.thumbnail) {
          formData.append('thumbnail', attachment.thumbnail);
        }
      });

      updateCommentPromises.push(KnowledgeAPI.updateCommentMedia(companyId, comment?.id, formData));
    }

    if (removedAttachments.length) {
      const removeAttachmentsPromises = removedAttachments.map((fileName) =>
        KnowledgeAPI.removeCommentMedia(companyId, comment.id, fileName),
      );
      updateCommentPromises.push(Promise.all(removeAttachmentsPromises));
    }

    const [res1, res2, res3] = await Promise.all(updateCommentPromises);

    let updatedComment: CommentType | CommentReplyType | undefined = shouldUpdateCommentText
      ? res1?.data?.comment || {}
      : res1?.data || res2?.data || comment;
    let attachments: AttachmentType[] = [];

    // collect new attachments
    if (shouldUpdateCommentText && newAttachments.length) {
      attachments = [...(res2?.data?.attachments || []), ...attachments];
    }
    if (!shouldUpdateCommentText && newAttachments.length) {
      attachments = [...(res1?.data?.attachments || []), ...attachments];
    }

    // collect updated attachments
    if (shouldUpdateCommentText && newAttachments.length && updatedAttachments.length) {
      attachments = [...(res3?.data?.attachments || []), ...attachments];
    }
    if (
      (shouldUpdateCommentText && newAttachments.length === 0 && updatedAttachments.length) ||
      (!shouldUpdateCommentText && newAttachments.length && updatedAttachments.length)
    ) {
      attachments = [...(res2?.data?.attachments || []), ...attachments];
    }
    if (!shouldUpdateCommentText && newAttachments.length === 0 && updatedAttachments.length) {
      attachments = [...(res1?.data?.attachments || []), ...attachments];
    }

    if (!_.isEmpty(updatedComment)) {
      // order attachments
      let finalAttachments = _.uniqBy(attachments, 'fileName');
      finalAttachments = editAttachments
        .filter((attachment) => !(attachment.fileUploaded && attachment.isRemoved))
        .map((attachment) => {
          return (
            attachments.find(
              (file) =>
                getExactFileName(file).toLowerCase() === getExactFileName(attachment).toLowerCase(),
            ) || attachment
          );
        });
      updatedComment = {
        ...updatedComment,
        attachments: finalAttachments,
      };

      updateCommentsCache(updatedComment, 'update', commentPath);
    }
  };

  const handleLikeComment = async (
    updatedComment: CommentType,
    commentPath: string,
    isUserLiked: LikedUserType | undefined,
  ) => {
    if (user) {
      if (isUserLiked) {
        await KnowledgeAPI.removeCommentLike(user?.companyId, updatedComment.id);
      } else {
        await KnowledgeAPI.likeComment(user?.companyId, updatedComment.id);
      }
      updateCommentsCache(updatedComment, 'like', commentPath);
    }
  };

  const handleDeleteComment = async (comment: CommentType, commentPath: string) => {
    await KnowledgeAPI.deleteComment(comment.id);
    updateCommentsCache(comment, 'delete', commentPath);
  };
  return (
    <Box sx={{ mb: 2 }} className="article-comments">
      <CommentsBox
        comments={comments}
        onAddCommentCallback={handleAddComment}
        onReplyCommentCallback={handleReplyComment}
        onUpdateCommentCallback={handleUpdateComment}
        onLikeCommentCallback={handleLikeComment}
        onDeleteCommentCallback={handleDeleteComment}
      />
    </Box>
  );
}

export default TaskCommentsBox;
